import {
  MutableRefObject,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';
import { UseFormReturn, useForm } from 'react-hook-form';

import {
  ButtonTextProps,
  StringIndexedObject,
  useToast,
} from '@kanda-libs/ks-design-library';
import {
  Credit,
  Job,
  JobCompanyInfo,
  SatNote,
  Service,
  actions,
  services,
  useSubmit,
} from '@kanda-libs/ks-frontend-services';
import { SatNoteCreditRequestBodyStruct } from '@kanda-libs/ks-frontend-services/src/service/external';

import { useAppDispatch } from 'components/App';
import useApiError from 'hooks/useApiError';
import { reloadInitialData } from 'store/slices/app';
import { cleanObject, scrollToTop } from 'utils';

import { DEFAULT_NEXT_BUTTON_PROPS } from '../constants';
import { SatNotePage, useSatNoteContext } from '../context';

interface FingerprintBoolean {
  value: boolean;
  timestamp: number;
  ip: string;
}

interface SatNoteFormQuestions {
  acceptedTerms: boolean;
  acknowledged: FingerprintBoolean;
  job: {
    sat_note: SatNote;
  };
}

interface SatNoteFormHook {
  form: UseFormReturn<SatNoteFormQuestions>;
  onSubmit: (data: StringIndexedObject<SatNoteFormQuestions>) => void;
  page: SatNotePage;
  isLoading: boolean;
  isSubmitting: boolean;
  isIntroduced: boolean;
  isSupplyOnly: boolean;
  nextButtonProps: ButtonTextProps;
  scrollRef: MutableRefObject<HTMLDivElement | null>;
}

export const formatFormValues = (
  formValues: SatNoteFormQuestions,
  job: Job,
): SatNoteFormQuestions => {
  const isIntroduced = Boolean(job?.introduced_to);
  if (!isIntroduced) return formValues;
  return {
    ...formValues,
    job: {
      sat_note: {
        ...formValues.job.sat_note,
        q_rate: 'No - I was not told to choose a specific option',
      },
    },
  };
};

export const formatSatNoteBody = (
  formValues: SatNoteFormQuestions,
  job: Job,
  credit: Credit,
  companyInfo: JobCompanyInfo,
): SatNoteCreditRequestBodyStruct => ({
  job,
  credit,
  companyInfo,
  satNote: {
    ...formValues.job.sat_note,
    q_availability: 'The installer told me when quoting me',
    q_application: 'I completed the application myself',
    q_advice: 'They just sent me a link to apply',
    address: cleanObject(formValues.job.sat_note.address ?? {}),
  },
  fingerprint: {
    value: formValues.acknowledged.value,
    ip: formValues.acknowledged.ip,
    timestamp: formValues.acknowledged.timestamp,
  },
});

export const getFileName = (job: Job, type: string): string =>
  `kanda-job${job?.id ? `-${job.id}` : ''}-${type}.pdf`;

export default function useSatNoteForm(
  onSuccess?: () => void,
): SatNoteFormHook {
  const {
    job,
    credit,
    companyInfo,
    page,
    toNextPage,
    isLoading,
    isIntroduced,
    isSupplyOnly,
  } = useSatNoteContext();
  const { showError } = useToast();

  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const scrollRef = useRef<HTMLDivElement | null>(null);

  const dispatch = useAppDispatch();

  const { submit: createSatNotePdf } = useSubmit(
    services.satnotev2.credit as unknown as Service<
      StringIndexedObject,
      StringIndexedObject,
      StringIndexedObject
    >,
  );

  const onSignError = useApiError('Error signing sat note at this time');

  const form = useForm<SatNoteFormQuestions>({
    mode: 'onBlur',
    reValidateMode: 'onBlur',
  });

  const onSubmit = useCallback(
    (formValues: StringIndexedObject<SatNoteFormQuestions>) => {
      if (!job || !credit || !companyInfo) {
        showError(
          'Job or Credit not available - please refresh and try again. If this error persists, please contact Ideal4Finance',
        );
        return;
      }
      const body = formatSatNoteBody(
        formValues as unknown as SatNoteFormQuestions,
        job,
        credit,
        companyInfo,
      );
      if (!body.satNote || !body.fingerprint.value) {
        showError(
          'Error encountered with form field values - please refresh and try again. If this error persists, please contact Ideal4Finance',
        );
        return;
      }
      setIsSubmitting(true);
      createSatNotePdf({
        body: { ...body },
      }).then(({ data: pdf, error: pdfError }) => {
        if (pdfError) {
          showError(
            typeof pdfError === 'string'
              ? pdfError
              : 'Error generating sat note at this time',
          );
          return;
        }
        if (!pdf || !pdf?.base64) {
          showError('Unknown error generating sat note at this time');
          return;
        }
        dispatch(
          actions.signJobSatNote({
            params: {
              id: job.id as string,
            },
            body: {
              ...body.satNote,
              certificate: {
                name: getFileName(job, 'sat-note'),
                content: pdf.base64,
              },
              signature: {
                signed: 'yes',
                fingerprint: body.fingerprint.ip,
              },
            },
            onError: (error) => {
              onSignError(error);
              setIsSubmitting(false);
            },
            onSuccess: () => {
              if (onSuccess) onSuccess();
              setIsSubmitting(false);
              reloadInitialData();
              scrollToTop();
            },
          }),
        );
      });
    },
    [
      onSuccess,
      createSatNotePdf,
      credit,
      companyInfo,
      dispatch,
      job,
      onSignError,
      showError,
    ],
  );

  const acceptedTerms = form.watch('acceptedTerms');
  const acknowledgedField = form.watch('acknowledged');
  const acknowledged = acknowledgedField?.value;

  const disabled = useMemo(() => {
    if (isLoading || isSubmitting) return true;
    if (page === 'terms' && !acceptedTerms) return true;
    if (page === 'questions' && !acknowledged) return true;
    return false;
  }, [isLoading, acceptedTerms, acknowledged, page, isSubmitting]);

  const nextButtonProps = useMemo(
    () => ({
      ...DEFAULT_NEXT_BUTTON_PROPS,
      ...(page === 'questions' && {
        label: 'Submit',
        submit: true,
      }),
      ...(page !== 'questions' && {
        onClick: () =>
          setTimeout(() => {
            toNextPage();
            if (!scrollRef.current) {
              scrollToTop();
              return;
            }
            const element = scrollRef.current;
            element.scrollIntoView({
              behavior: 'smooth',
            });
          }, 10),
      }),
      id: `${DEFAULT_NEXT_BUTTON_PROPS.id}-${page}`,
      disabled,
    }),
    [page, toNextPage, disabled],
  );

  return {
    form,
    onSubmit,
    page,
    isLoading,
    isSubmitting,
    isIntroduced,
    isSupplyOnly,
    nextButtonProps,
    scrollRef,
  };
}
